import { z } from "zod";

export const channelMediumSchema = z.enum(["email", "rcs", "sms", "mms"]);

export const textTransportMediumSchema = z.enum(["sms", "mms", "rcs"]);

export const editorTypeSchema = z.enum([
  "grapesjs_email",
  "grapesjs_plaintext_email",
  "grapesjs_html",
  "html",
  "true_plaintext_email",
]);

export const reviewConfigSchema = z.object({
  max_reviews: z.number(),
  min_score: z.number(),
  verified_buyer_only: z.boolean(),
  sort_by: z.string(),
});

// Per-pick payload for a static grid: identifiers only, never a snapshot of
// display fields — the backend resolves name/price/image at send time.
// vendor_variant_id is the resolution key the send-time consumer looks a
// pick up by; vendor_product_id is context only (grouping, logging,
// fallback). This assertion is a save-boundary backstop, not the primary
// enforcement: `getStaticPicks` already filters a variant-less pick out
// upstream, so this only ever fires if a caller builds a slice by hand.
export const productGridPickSchema = z.object({
  vendor_product_id: z.string(),
  vendor_variant_id: z.string().min(1),
});

export const productGridConfigSchema = z.object({
  items: z.string(),
  max_products: z.number(),
  static_products: z.array(productGridPickSchema).optional(),
  reviews: reviewConfigSchema.optional(),
  show_source_badge: z.boolean().optional(),
});

/** Offer kinds a smart offer can prefer (offer-bank wire values, CON-344 Q8). */
export const smartOfferCategoryHintSchema = z.enum([
  "PERCENT_OFF",
  "DOLLAR_OFF",
  "BOGO",
  "FREE_SHIPPING",
  "OTHER",
]);

export type SmartOfferCategoryHint = z.infer<
  typeof smartOfferCategoryHintSchema
>;

export const offersConfigSchema = z.record(
  z.string(),
  z.object({
    fallback_code: z.string(),
    /** Present on `smart:<id>` keys: the backend picks the offer per recipient. */
    smart: z
      .object({
        // An unknown kind is dropped, not a failed save.
        category_hint: smartOfferCategoryHintSchema.optional().catch(undefined),
      })
      .optional(),
  }),
);

export const frameVariantConfigSchema = z.object({
  subject_line: z.string().optional(),
  preview_text: z.string().optional(),
  product_grids: z.record(z.string(), productGridConfigSchema).optional(),
  offers: offersConfigSchema.optional(),
});

const baseVariantFields = {
  content_text: z.string().nullable(),
  content_url: z.string().nullable(),
  content_json: z.unknown().nullable(),
  // Plain-text email body; when set without HTML the backend sends text/plain
  content_plaintext: z.string().nullable().optional(),
  config: frameVariantConfigSchema.optional(),
};

export const emailCreateVariantSchema = z.object({
  ...baseVariantFields,
  medium: z.literal("email"),
  editor_type: editorTypeSchema,
});

const textEditorTypeSchema = z.preprocess(
  (v) => (v === "" ? null : v),
  editorTypeSchema.nullable().optional(),
);

const textCreateVariantBase = z.object({
  ...baseVariantFields,
  editor_type: textEditorTypeSchema,
});

export const createFrameVariantPayloadSchema = z.discriminatedUnion("medium", [
  emailCreateVariantSchema,
  textCreateVariantBase.extend({ medium: z.literal("sms") }),
  textCreateVariantBase.extend({ medium: z.literal("mms") }),
  textCreateVariantBase.extend({ medium: z.literal("rcs") }),
]);

export const createFramePayloadSchema = z.object({
  name: z.string().trim().min(1),
  is_active: z.boolean(),
  frame_variants: z.array(createFrameVariantPayloadSchema).min(1),
  site: z.string().min(1),
  inner_tile: z.string(),
  metadata: z.record(z.unknown()).optional(),
});

const existingVariantFieldsSchema = z.object({
  id: z.string(),
  frame: z.string(),
  email: z.string().nullable(),
  create_date: z.string(),
  modify_date: z.string(),
  deleted_date: z.string().nullable(),
});

export const emailUpdateVariantSchema = emailCreateVariantSchema.merge(
  existingVariantFieldsSchema.partial(),
);

const textUpdateVariantBase = textCreateVariantBase.merge(
  existingVariantFieldsSchema.partial(),
);

export const updateFrameVariantSchema = z.union([
  emailUpdateVariantSchema,
  textUpdateVariantBase.extend({ medium: z.literal("sms") }),
  textUpdateVariantBase.extend({ medium: z.literal("mms") }),
  textUpdateVariantBase.extend({ medium: z.literal("rcs") }),
]);

export const updateFramePayloadSchema = z
  .object({
    name: z.string().trim().min(1).optional(),
    is_active: z.boolean().optional(),
    frame_variants: z.array(updateFrameVariantSchema).optional(),
    status: z.string().optional(),
    approval_status: z.enum(["pending", "approved"]).optional(),
    slug: z.string().optional(),
    inner_tile: z.string().min(1).optional(),
    metadata: z.record(z.unknown()).optional(),
  })
  .refine((data) => Object.keys(data).length > 0, {
    message: "Update payload must contain at least one field",
  });

export type ReviewConfigParsed = z.infer<typeof reviewConfigSchema>;
export type ProductGridPickParsed = z.infer<typeof productGridPickSchema>;
export type ProductGridConfigParsed = z.infer<typeof productGridConfigSchema>;
export type OffersConfigParsed = z.infer<typeof offersConfigSchema>;
export type FrameVariantConfigParsed = z.infer<typeof frameVariantConfigSchema>;

export type CreateFrameVariantPayloadParsed = z.infer<
  typeof createFrameVariantPayloadSchema
>;
export type CreateFramePayloadParsed = z.infer<typeof createFramePayloadSchema>;
export type UpdateFrameVariantParsed = z.infer<typeof updateFrameVariantSchema>;
export type UpdateFramePayloadParsed = z.infer<typeof updateFramePayloadSchema>;
